import {
  type EnvironmentId,
  type ProjectId,
  type ServerProviderSkill,
  ThreadId,
  type TurnId,
} from "@t3tools/contracts";
import {
  createContext,
  memo,
  use,
  useEffect,
  Fragment,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTimelineScroll, type TimelineScrollHandle } from "./useTimelineScroll";
import { scopeThreadRef } from "@t3tools/client-runtime";
import {
  deriveChildTaskStatus,
  type TimelineEntry,
  formatClock,
  formatElapsed,
  type WorkLogEntry,
} from "../../session-logic";
import { selectEnvironmentState, selectRunningSubAgentChildCount, useStore } from "../../store";
import { openSubAgentList, openSubAgentPanelForEntry } from "../../subAgentPanelStore";
import { taskStatusLabel, type TaskDisplayStatus } from "./taskStatusLabels";
import { type ChatMessage } from "../../types";
import ChatMarkdown from "../ChatMarkdown";
import {
  BotIcon,
  CircleAlertIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DatabaseIcon,
  FilePenIcon,
  ImageIcon,
  type LucideIcon,
  PuzzleIcon,
  SearchIcon,
  ShrinkIcon,
  TerminalIcon,
} from "lucide-react";
import { Button } from "../ui/button";
import { type ExpandedImagePreview } from "./ExpandedImagePreview";
import { AttachmentsBlock } from "./AttachmentsBlock";
import { MessageCopyButton } from "./MessageCopyButton";
import {
  groupWorkLogEntries,
  computeStableMessagesTimelineRows,
  deriveMessagesTimelineRows,
  normalizeCompactToolLabel,
  resolveAssistantMessageCopyState,
  type StableMessagesTimelineRowsState,
  type MessagesTimelineRow,
  type TurnGroupItem,
  type TurnStatus,
  type WorkLogGroup,
} from "./MessagesTimeline.logic";
import { turnFailureMessageKey } from "./inferenceFailureMessages";
import { useInferenceCredentialStatus } from "../../environments/primary/inferenceCredential";
import { LiveText } from "./LiveText";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { cn } from "~/lib/utils";

import { formatWorkspaceRelativePath } from "../../filePathDisplay";
import { Trans, useTranslation } from "react-i18next";
import { t } from "~/i18n";
import type { I18nKey } from "~/i18n";
import type { i18n as I18nInstance } from "i18next";

// ---------------------------------------------------------------------------
// Context — shared state consumed by every row component via Context.
// Propagates through row memo boundaries for shared callbacks and
// non-row-scoped state. `nowIso` is intentionally excluded — self-ticking
// components (WorkingTimer, LiveElapsed) handle it.
// ---------------------------------------------------------------------------

interface TimelineRowSharedState {
  routeThreadKey: string;
  markdownCwd: string | undefined;
  resolvedTheme: "light" | "dark";
  workspaceRoot: string | undefined;
  skills: ReadonlyArray<Pick<ServerProviderSkill, "name" | "displayName">>;
  projectId: ProjectId | undefined;
  activeThreadEnvironmentId: EnvironmentId;
  timelineThreadId: ThreadId;
  onImageExpand: (preview: ExpandedImagePreview) => void;
}

interface TimelineRowActivityState {
  isWorking: boolean;
}

const TimelineRowCtx = createContext<TimelineRowSharedState>(null!);
const TimelineRowActivityCtx = createContext<TimelineRowActivityState>(null!);
const TIMELINE_LIST_HEADER = <div className="h-3 sm:h-4" />;
const TIMELINE_LIST_FOOTER = <div className="h-3 sm:h-4" />;

const EMPTY_TIMELINE_SKILLS: ReadonlyArray<Pick<ServerProviderSkill, "name" | "displayName">> = [];

// ---------------------------------------------------------------------------
// Props (public API)
// ---------------------------------------------------------------------------

interface MessagesTimelineProps {
  isWorking: boolean;
  workingLabel?: string | null;
  activeTurnInProgress: boolean;
  activeTurnId?: TurnId | null;
  activeTurnStartedAt: string | null;
  timelineRef?: React.RefObject<TimelineScrollHandle | null>;
  timelineEntries: TimelineEntry[];
  routeThreadKey: string;
  onImageExpand: (preview: ExpandedImagePreview) => void;
  activeThreadEnvironmentId: EnvironmentId;
  projectId: ProjectId | undefined;
  timelineThreadId: ThreadId;
  markdownCwd: string | undefined;
  resolvedTheme: "light" | "dark";
  workspaceRoot: string | undefined;
  skills?: ReadonlyArray<Pick<ServerProviderSkill, "name" | "displayName">>;
  onIsAtEndChange: (isAtEnd: boolean) => void;
  hasMoreBefore: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
  emptyState?: ReactNode;
  /** The thread's snapshot has not arrived yet: the timeline shows the loading state instead of rows. */
  loading?: boolean;
  loadingDelayMs?: number;
}

// ---------------------------------------------------------------------------
// MessagesTimeline — list owner
// ---------------------------------------------------------------------------

export const MessagesTimeline = memo(function MessagesTimeline({
  isWorking,
  workingLabel = null,
  activeTurnInProgress,
  activeTurnId,
  activeTurnStartedAt,
  timelineRef,
  timelineEntries,
  routeThreadKey,
  onImageExpand,
  activeThreadEnvironmentId,
  projectId,
  timelineThreadId,
  markdownCwd,
  resolvedTheme,
  workspaceRoot,
  skills = EMPTY_TIMELINE_SKILLS,
  onIsAtEndChange,
  hasMoreBefore,
  loadingOlder,
  onLoadOlder,
  emptyState,
  loading = false,
  loadingDelayMs,
}: MessagesTimelineProps) {
  const rawRows = useMemo(
    () =>
      deriveMessagesTimelineRows({
        timelineEntries,
        isWorking,
        workingLabel,
        activeTurnInProgress,
        activeTurnId: activeTurnId ?? null,
        activeTurnStartedAt,
      }),
    [
      timelineEntries,
      isWorking,
      workingLabel,
      activeTurnInProgress,
      activeTurnId,
      activeTurnStartedAt,
    ],
  );
  const rows = useStableRows(rawRows);

  const { scrollerRef, contentRef, onScroll } = useTimelineScroll({
    handleRef: timelineRef,
    onIsAtEndChange,
    layoutKey: rows,
  });

  const sharedState = useMemo<TimelineRowSharedState>(
    () => ({
      routeThreadKey,
      markdownCwd,
      resolvedTheme,
      projectId,
      workspaceRoot,
      skills,
      activeThreadEnvironmentId,
      timelineThreadId,
      onImageExpand,
    }),
    [
      routeThreadKey,
      markdownCwd,
      resolvedTheme,
      projectId,
      workspaceRoot,
      skills,
      activeThreadEnvironmentId,
      timelineThreadId,
      onImageExpand,
    ],
  );
  const activityState = useMemo<TimelineRowActivityState>(() => ({ isWorking }), [isWorking]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const onLoadOlderRef = useRef(onLoadOlder);
  onLoadOlderRef.current = onLoadOlder;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMoreBefore) {
      return;
    }
    const root = node.closest("[data-timeline-scroll]");
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadOlderRef.current();
        }
      },
      { root, rootMargin: "200px 0px 0px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMoreBefore, loadingOlder]);

  const listHeader = (
    <div>
      {hasMoreBefore ? (
        <div
          ref={sentinelRef}
          data-timeline-older-sentinel="true"
          className="flex justify-center py-3 text-xs text-muted-foreground/50"
        >
          {loadingOlder ? t("chat/loadingEarlierMessages_o0n6y6") : null}
        </div>
      ) : null}
      {TIMELINE_LIST_HEADER}
    </div>
  );

  if (loading) {
    return <TimelineLoadingMessage delayMs={loadingDelayMs} />;
  }

  if (rows.length === 0 && !isWorking) {
    return (
      emptyState ?? <TimelineEmptyMessage>{t("chat/sendAMessageTo_gjdaf3")}</TimelineEmptyMessage>
    );
  }

  return (
    <TimelineRowCtx value={sharedState}>
      <TimelineRowActivityCtx value={activityState}>
        <div
          ref={scrollerRef}
          onScroll={onScroll}
          data-timeline-scroll="true"
          className="h-full overflow-y-auto overflow-x-hidden overscroll-y-contain px-3 [overflow-anchor:none] sm:px-5"
        >
          <div ref={contentRef} className="relative">
            {listHeader}
            {rows.map((row) => (
              <div
                key={row.id}
                className="mx-auto w-full min-w-0 max-w-3xl overflow-x-clip"
                data-timeline-root="true"
              >
                <TimelineRowContent row={row} />
              </div>
            ))}
            {TIMELINE_LIST_FOOTER}
          </div>
        </div>
      </TimelineRowActivityCtx>
    </TimelineRowCtx>
  );
});

export function TimelineEmptyMessage({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center">
      <p className="text-sm text-muted-foreground/30">{children}</p>
    </div>
  );
}

const TIMELINE_LOADING_DELAY_MS = 150;

/** Shown once the snapshot has been pending for `delayMs`, so an instant load never flashes it. */
export function TimelineLoadingMessage({
  delayMs = TIMELINE_LOADING_DELAY_MS,
}: {
  delayMs?: number | undefined;
}) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(delayMs === 0);
  useEffect(() => {
    if (delayMs === 0) return;
    const timer = window.setTimeout(() => setVisible(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs]);
  return (
    <div className="flex h-full items-center justify-center" data-timeline-loading="true">
      {visible ? (
        <p className="thread-loading-pulse text-sm" role="status">
          {t("chat/loadingChat_1ypzxo")}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TimelineRowContent — the actual row component
// ---------------------------------------------------------------------------

type TimelineWorkEntry = WorkLogEntry;
type TimelineRow = MessagesTimelineRow;
type TurnSectionItem = Extract<TurnGroupItem, { kind: "section" }>;

const TimelineRowContent = memo(function TimelineRowContent({ row }: { row: TimelineRow }) {
  return (
    <div
      className={cn("pb-4", row.kind === "message" ? "group/message" : null)}
      data-timeline-row-id={row.id}
      data-timeline-row-kind={row.kind}
      data-message-id={row.kind === "message" ? row.message.id : undefined}
      data-message-role={row.kind === "message" ? row.message.role : undefined}
    >
      {row.kind === "message" && row.message.role === "user" ? <UserTimelineRow row={row} /> : null}
      {row.kind === "message" && row.message.role === "assistant" ? (
        <AssistantTimelineRow row={row} />
      ) : null}
      {row.kind === "turn-failed" ? <TurnFailureTimelineRow row={row} /> : null}
      {row.kind === "turn-group" ? <TurnGroupTimelineRow row={row} /> : null}
      {row.kind === "working" ? <WorkingTimelineRow row={row} /> : null}
    </div>
  );
});

function TurnFailureTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "turn-failed" }> }) {
  const { t } = useTranslation();
  const credentialStatus = useInferenceCredentialStatus((state) => state.status);
  return (
    <div className="flex items-start gap-2 text-sm text-destructive">
      <CircleAlertIcon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <p>{t(turnFailureMessageKey(row.reason, credentialStatus))}</p>
    </div>
  );
}

function UserTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "message" }> }) {
  const ctx = use(TimelineRowCtx);
  const userAttachments = row.message.attachments ?? [];
  const hasText = row.message.text.trim().length > 0;

  return (
    <div className="flex flex-col items-end gap-2">
      <AttachmentsBlock
        attachments={userAttachments}
        variant="message"
        onExpandImage={ctx.onImageExpand}
        className="w-full"
      />
      {hasText ? (
        <>
          <div
            className="relative max-w-[80%] rounded-2xl rounded-br-sm border border-border bg-secondary px-4 py-3"
            data-user-message-bubble="true"
          >
            <CollapsibleUserMessageBody
              text={row.message.text}
              skills={ctx.skills}
              environmentId={ctx.activeThreadEnvironmentId}
              markdownCwd={ctx.markdownCwd}
              projectId={ctx.projectId}
            />
          </div>
          <MessageActions text={row.message.text} className="-mt-0.5 pr-1" />
        </>
      ) : null}
    </div>
  );
}

function AssistantTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "message" }> }) {
  const { t } = useTranslation();
  const ctx = use(TimelineRowCtx);
  const messageText =
    row.message.text || (row.message.streaming ? "" : t("chat/emptyResponse_pq5peh"));

  return (
    <div className="min-w-0 px-1 py-0.5">
      <ChatMarkdown
        text={messageText}
        cwd={ctx.markdownCwd}
        projectId={ctx.projectId}
        environmentId={ctx.activeThreadEnvironmentId}
        isStreaming={Boolean(row.message.streaming)}
        skills={ctx.skills}
      />
      <div className="mt-1.5 flex items-center">
        <AssistantCopyButton row={row} />
      </div>
    </div>
  );
}

/** Everything the agent produced before its final answer, split into stages
 *  and collapsed under the turn's elapsed time. Owns its expand state so
 *  toggling re-renders only this row. */
const TurnGroupTimelineRow = memo(function TurnGroupTimelineRow({
  row,
}: {
  row: Extract<TimelineRow, { kind: "turn-group" }>;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const elapsed = row.endedAt ? formatElapsed(row.startedAt, row.endedAt) : null;
  const settledLabel = elapsed
    ? t("common/workedFor_6868ug", { elapsed })
    : t("chat/workLog_asrz8c");

  if (row.live) {
    return (
      <div data-turn-group="true" data-turn-group-expanded="true" data-turn-group-live="true">
        <TurnItemsFeed items={row.items} status={row.status} startedAt={row.startedAt} />
      </div>
    );
  }

  return (
    <div
      data-turn-group="true"
      data-turn-group-expanded={expanded ? "true" : "false"}
      data-turn-group-live="false"
    >
      <button
        type="button"
        aria-expanded={expanded}
        data-scroll-anchor-ignore
        onClick={() => setExpanded((value) => !value)}
        className="flex items-center gap-1 py-1 text-sm text-muted-foreground/80 transition-colors duration-150 hover:text-foreground"
      >
        <span>{settledLabel}</span>
        <ChevronRightIcon
          aria-hidden="true"
          className={cn("size-3.5 transition-transform duration-200", expanded && "rotate-90")}
        />
      </button>
      {expanded ? (
        <div className="mt-2">
          <TurnItemsFeed items={row.items} status={row.status} startedAt={row.startedAt} />
        </div>
      ) : null}
      <div className="mt-2 h-px bg-border" />
    </div>
  );
});

function TurnItemsFeed({
  items,
  status,
  startedAt,
}: {
  items: ReadonlyArray<TurnGroupItem>;
  status: TurnStatus | null;
  startedAt: string;
}) {
  const ctx = use(TimelineRowCtx);
  return (
    <div className="space-y-2" data-turn-group-items="true">
      {items.map((item) =>
        item.kind === "section" ? (
          <TurnSectionBlock key={item.id} item={item} />
        ) : item.kind === "question" ? (
          <TurnQuestionBlock key={item.id} message={item.message} />
        ) : (
          <div key={item.id} className="min-w-0 px-1 py-0.5">
            <ChatMarkdown
              text={item.message.text}
              cwd={ctx.markdownCwd}
              projectId={ctx.projectId}
              environmentId={ctx.activeThreadEnvironmentId}
              isStreaming={Boolean(item.message.streaming)}
              skills={ctx.skills}
            />
          </div>
        ),
      )}
      {status ? <TurnStatusRow status={status} startedAt={startedAt} /> : null}
    </div>
  );
}

/** A question the agent asked without pausing; it stays in the feed at its place. */
const TurnQuestionBlock = memo(function TurnQuestionBlock({ message }: { message: ChatMessage }) {
  const ctx = use(TimelineRowCtx);
  const { t } = useTranslation();
  return (
    <div
      className="min-w-0 border-l-2 border-border py-0.5 pr-1 pl-3"
      data-turn-question="true"
      data-turn-question-id={message.id}
    >
      <div className="text-xs text-muted-foreground/80">{t("chat/question_48pygg")}</div>
      <ChatMarkdown
        text={message.text}
        cwd={ctx.markdownCwd}
        projectId={ctx.projectId}
        environmentId={ctx.activeThreadEnvironmentId}
        isStreaming={Boolean(message.streaming)}
        skills={ctx.skills}
      />
    </div>
  );
});

const TurnSectionBlock = memo(function TurnSectionBlock({ item }: { item: TurnSectionItem }) {
  const ctx = use(TimelineRowCtx);
  const rows = useMemo(
    () => groupWorkLogEntries(item.entries, item.open),
    [item.entries, item.open],
  );
  return (
    <div
      data-turn-section="true"
      data-turn-section-expanded="true"
      data-turn-section-kind={item.message ? "commentary" : "synthetic"}
    >
      {item.message ? (
        <div className="min-w-0 px-1 py-0.5" data-turn-section-heading="full">
          <ChatMarkdown
            text={item.message.text}
            cwd={ctx.markdownCwd}
            projectId={ctx.projectId}
            environmentId={ctx.activeThreadEnvironmentId}
            isStreaming={Boolean(item.message.streaming)}
            skills={ctx.skills}
          />
        </div>
      ) : null}
      <div className="mt-1 space-y-0.5 pl-5" data-turn-section-entries="true">
        {rows.map((row) =>
          row.kind === "group" ? (
            <WorkEntryGroup key={`work-group:${row.id}`} group={row} />
          ) : (
            <WorkEntryRow key={`work-row:${row.entry.id}`} workEntry={row.entry} />
          ),
        )}
      </div>
    </div>
  );
});

/** The turn's running clock, always on while the turn is live; the state adds its own words. */
function TurnStatusRow({ status, startedAt }: { status: TurnStatus; startedAt: string }) {
  const { t } = useTranslation();
  const { activeThreadEnvironmentId, timelineThreadId } = use(TimelineRowCtx);
  const threadRef = scopeThreadRef(activeThreadEnvironmentId, timelineThreadId);
  const runningAgents = useStore((state) => selectRunningSubAgentChildCount(state, threadRef));
  const elapsed = useElapsedClock(startedAt);
  const waiting =
    status.kind === "waiting_helpers" || (status.kind === "thinking" && runningAgents > 0);
  const kind: TurnStatus["kind"] = waiting ? "waiting_helpers" : status.kind;
  const waitingLabel = status.kind === "waiting_helpers" ? status.entries[0]?.label : null;
  const agentCount = runningAgents > 0 ? ` (${runningAgents})` : "";

  return (
    <div className="pl-5" data-turn-status={kind}>
      {status.kind === "acting" ? (
        <div className="space-y-0.5">
          {status.entries.map((workEntry) => (
            <WorkEntryRow key={`work-row:${workEntry.id}`} workEntry={workEntry} />
          ))}
        </div>
      ) : null}
      <div
        className="flex items-center gap-2 py-0.5 pt-1 text-sm text-muted-foreground/70"
        data-turn-clock="true"
      >
        <span className="tabular-nums" data-turn-status-elapsed="true">
          {elapsed}
        </span>
        {kind === "thinking" ? (
          <span className="thinking-ellipsis">{t("chat/thinking_qmtzah")}</span>
        ) : null}
        {waiting ? (
          <button
            type="button"
            className="cursor-pointer underline-offset-2 hover:text-foreground hover:underline"
            onClick={() => openSubAgentList(threadRef)}
          >
            {waitingLabel ?? t("chat/waitingForHelpers_bye6k3")}
            {agentCount}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function useElapsedClock(startedAt: string): string {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1_000);
    return () => clearInterval(id);
  }, []);
  const startedMs = Date.parse(startedAt);
  return formatClock(Number.isFinite(startedMs) ? nowMs - startedMs : 0);
}

function PulsingDots() {
  return (
    <span className="inline-flex items-center gap-[3px]">
      <span className="h-1 w-1 rounded-full bg-muted-foreground/30 animate-pulse" />
      <span className="h-1 w-1 rounded-full bg-muted-foreground/30 animate-pulse [animation-delay:200ms]" />
      <span className="h-1 w-1 rounded-full bg-muted-foreground/30 animate-pulse [animation-delay:400ms]" />
    </span>
  );
}

function AssistantCopyButton({ row }: { row: Extract<TimelineRow, { kind: "message" }> }) {
  const assistantCopyState = resolveAssistantMessageCopyState({
    text: row.message.text ?? null,
    showCopyButton: row.showAssistantCopyButton,
    streaming: row.assistantCopyStreaming,
  });

  if (!assistantCopyState.visible) {
    return null;
  }

  return <MessageActions text={assistantCopyState.text ?? ""} />;
}

function MessageActions({ text, className }: { text: string; className?: string }) {
  return (
    <div
      className={cn(
        "flex items-center opacity-0 transition-opacity duration-200 focus-within:opacity-100 group-hover/message:opacity-100",
        className,
      )}
      data-message-actions="true"
    >
      <MessageCopyButton
        text={text}
        size="icon-xs"
        variant="outline"
        className="border-border/50 bg-background/35 text-muted-foreground/45 shadow-none hover:border-border/70 hover:bg-background/55 hover:text-muted-foreground/70"
      />
    </div>
  );
}

function WorkingTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "working" }> }) {
  const { activeThreadEnvironmentId, timelineThreadId } = use(TimelineRowCtx);
  const threadRef = scopeThreadRef(activeThreadEnvironmentId, timelineThreadId);
  const runningAgents = useStore((state) => selectRunningSubAgentChildCount(state, threadRef));
  const segments: Array<{ key: string; content: ReactNode }> = [
    {
      key: "elapsed",
      content: row.createdAt ? (
        <Trans
          i18nKey="chat/workingFor_cqeqlw"
          components={[<WorkingTimer key="0" createdAt={row.createdAt} />]}
        />
      ) : (
        t("chat/working_gj81rh")
      ),
    },
  ];
  if (row.label) {
    segments.push({ key: "label", content: row.label });
  }
  if (runningAgents > 0) {
    segments.push({
      key: "agents",
      content: (
        <button
          type="button"
          className="cursor-pointer underline-offset-2 hover:text-foreground hover:underline"
          onClick={() => openSubAgentList(threadRef)}
        >
          {t("chat/agentWorking_lzio11", { count: runningAgents })}
        </button>
      ),
    });
  }

  return (
    <div className="py-0.5 pl-1.5">
      <div className="flex items-center gap-2 pt-1 text-sm text-muted-foreground/70">
        <PulsingDots />
        <span>
          {segments.map((segment, index) => (
            <Fragment key={segment.key}>
              {index > 0 ? " · " : null}
              {segment.content}
            </Fragment>
          ))}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Self-ticking labels — update their own text nodes so elapsed-time display
// does not create a React commit every second while a response is streaming.
// ---------------------------------------------------------------------------

/** Live "Working for Xs" label. */
function WorkingTimer({ createdAt }: { createdAt: string }) {
  return <LiveText compute={() => formatWorkingTimerNow(createdAt)} />;
}

// ---------------------------------------------------------------------------
// Leaf components
// ---------------------------------------------------------------------------

const MAX_COLLAPSED_USER_MESSAGE_LINES = 8;
const MAX_COLLAPSED_USER_MESSAGE_LENGTH = 600;
const COLLAPSED_USER_MESSAGE_FADE_HEIGHT_REM = 1.75;
const COLLAPSED_USER_MESSAGE_FADE_MASK = `linear-gradient(to bottom, black calc(100% - ${COLLAPSED_USER_MESSAGE_FADE_HEIGHT_REM}rem), transparent)`;

function shouldCollapseUserMessage(text: string): boolean {
  if (text.trim().length === 0) {
    return false;
  }

  return (
    text.length > MAX_COLLAPSED_USER_MESSAGE_LENGTH ||
    text.split("\n").length > MAX_COLLAPSED_USER_MESSAGE_LINES
  );
}

const CollapsibleUserMessageBody = memo(function CollapsibleUserMessageBody(props: {
  text: string;
  skills: ReadonlyArray<Pick<ServerProviderSkill, "name" | "displayName">>;
  environmentId: EnvironmentId;
  markdownCwd: string | undefined;
  projectId: ProjectId | undefined;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const canCollapse = shouldCollapseUserMessage(props.text);
  const isCollapsed = canCollapse && !expanded;

  return (
    <div>
      <div
        className={cn("relative", isCollapsed && "max-h-44 overflow-hidden")}
        data-user-message-body="true"
        data-user-message-collapsed={isCollapsed ? "true" : "false"}
        data-user-message-collapsible={canCollapse ? "true" : "false"}
        data-user-message-fade={isCollapsed ? "true" : "false"}
        style={
          isCollapsed
            ? {
                WebkitMaskImage: COLLAPSED_USER_MESSAGE_FADE_MASK,
                maskImage: COLLAPSED_USER_MESSAGE_FADE_MASK,
              }
            : undefined
        }
      >
        <UserMessageBody
          text={props.text}
          skills={props.skills}
          environmentId={props.environmentId}
          markdownCwd={props.markdownCwd}
          projectId={props.projectId}
        />
      </div>
      {canCollapse ? (
        <div className="mt-1.5 flex items-center" data-user-message-footer="true">
          <Button
            type="button"
            size="xs"
            variant="ghost"
            aria-expanded={expanded}
            data-scroll-anchor-ignore
            onClick={() => setExpanded((value) => !value)}
            className="-ml-1 h-6 rounded-md px-1.5 text-xs text-muted-foreground/72 hover:bg-muted/55 hover:text-foreground/85"
          >
            {expanded ? t("common/showLess_to2zoz") : t("settings/showFullMessage_g8m474")}
            <ChevronDownIcon
              aria-hidden="true"
              className={cn("size-3.5 transition-transform duration-200", expanded && "rotate-180")}
            />
          </Button>
        </div>
      ) : null}
    </div>
  );
});

const UserMessageBody = memo(function UserMessageBody(props: {
  text: string;
  skills: ReadonlyArray<Pick<ServerProviderSkill, "name" | "displayName">>;
  environmentId: EnvironmentId;
  markdownCwd: string | undefined;
  projectId: ProjectId | undefined;
}) {
  if (props.text.length === 0) {
    return null;
  }

  return (
    <ChatMarkdown
      text={props.text}
      cwd={props.markdownCwd}
      projectId={props.projectId}
      environmentId={props.environmentId}
      skills={props.skills}
      className="text-foreground"
      lineBreaks
    />
  );
});

// ---------------------------------------------------------------------------
// Structural sharing — reuse old row references when data hasn't changed
// so React can skip re-rendering unchanged rows.
// ---------------------------------------------------------------------------

/** Returns a structurally-shared copy of `rows`: for each row whose content
 *  hasn't changed since last call, the previous object reference is reused. */
function useStableRows(rows: MessagesTimelineRow[]): MessagesTimelineRow[] {
  const prevState = useRef<StableMessagesTimelineRowsState>({
    byId: new Map<string, MessagesTimelineRow>(),
    result: [],
  });

  return useMemo(() => {
    const nextState = computeStableMessagesTimelineRows(rows, prevState.current);
    prevState.current = nextState;
    return nextState.result;
  }, [rows]);
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

function formatWorkingTimer(startIso: string, endIso: string): string | null {
  const startedAtMs = Date.parse(startIso);
  const endedAtMs = Date.parse(endIso);
  if (!Number.isFinite(startedAtMs) || !Number.isFinite(endedAtMs)) {
    return null;
  }

  const elapsedSeconds = Math.max(0, Math.floor((endedAtMs - startedAtMs) / 1000));
  if (elapsedSeconds < 60) {
    const seconds = elapsedSeconds;
    return t("common/s_tqgrlz", { seconds });
  }

  const hours = Math.floor(elapsedSeconds / 3600);
  const minutes = Math.floor((elapsedSeconds % 3600) / 60);
  const seconds = elapsedSeconds % 60;

  if (hours > 0) {
    return minutes > 0
      ? `${t("common/h_lrzvx9", { hours })} ${t("common/m_dv7blw", { minutes })}`
      : t("common/h_lrzvx9", { hours });
  }

  return seconds > 0
    ? `${t("common/m_dv7blw", { minutes })} ${t("common/s_tqgrlz", { seconds })}`
    : t("common/m_dv7blw", { minutes });
}

function formatWorkingTimerNow(startIso: string): string {
  const seconds = 0;
  return (
    formatWorkingTimer(startIso, new Date().toISOString()) ?? t("common/s_tqgrlz", { seconds })
  );
}

function workToneClass(tone: "thinking" | "tool" | "info" | "error"): string {
  return tone === "error" ? "text-rose-300/50 dark:text-rose-300/50" : "text-muted-foreground/70";
}

function workEntryPreview(
  workEntry: Pick<TimelineWorkEntry, "detail" | "command" | "changedFiles">,
  workspaceRoot: string | undefined,
) {
  if (workEntry.command) return workEntry.command;
  if (workEntry.detail) return workEntry.detail;
  if ((workEntry.changedFiles?.length ?? 0) === 0) return null;
  const [firstPath] = workEntry.changedFiles ?? [];
  if (!firstPath) return null;
  const displayPath = formatWorkspaceRelativePath(firstPath, workspaceRoot);
  return workEntry.changedFiles!.length === 1
    ? displayPath
    : `${displayPath} +${workEntry.changedFiles!.length - 1} more`;
}

function workEntryRawCommand(
  workEntry: Pick<TimelineWorkEntry, "command" | "rawCommand">,
): string | null {
  const rawCommand = workEntry.rawCommand?.trim();
  if (!rawCommand || !workEntry.command) {
    return null;
  }
  return rawCommand === workEntry.command.trim() ? null : rawCommand;
}

function capitalizePhrase(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return value;
  }
  return `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}`;
}

const TOOL_ACTIVITY_LABELS: ReadonlyMap<string, I18nKey> = new Map([
  ["ran command", "chat/ranCommand_9g9bxk"],
  ["command run", "chat/commandRun_rrtc45"],
  ["file change", "chat/fileChange_wfqcov"],
  ["changed files", "chat/changedFiles_zpbcsa"],
  ["read file", "chat/readFile_vdycp8"],
  ["searched files", "chat/searchedFiles_2vc04i"],
  ["web search", "chat/webSearch_xnzpl6"],
  ["image view", "chat/imageView_b79ojw"],
  ["mcp tool call", "chat/mcpToolCall_7rhzbf"],
  ["tool call", "chat/toolCall_e07q7m"],
  ["subagent task", "chat/subagentTask_tn8bz7"],
  ["tool", "chat/tool_ennfgn"],
  ["reasoning", "chat/reasoning_fv4smg"],
  ["plan", "common/plan_dcp9rf"],
  ["error", "common/error_z8ql4u"],
  ["item", "chat/item_m9uybr"],
  ["command approval requested", "chat/commandApprovalRequested_yk02t1"],
  ["file-read approval requested", "chat/fileReadApprovalRequested_bqwkjy"],
  ["file-change approval requested", "chat/fileChangeApprovalRequested_0tjbfh"],
  ["approval resolved", "chat/approvalResolved_3lwq7u"],
  ["runtime error", "chat/runtimeError_a7i6tn"],
  ["plan updated", "chat/planUpdated_bceaf1"],
  ["user input requested", "chat/userInputRequested_zu8gjy"],
  ["user input submitted", "chat/userInputSubmitted_hwztzm"],
  ["reasoning update", "chat/reasoningUpdate_t7k98g"],
  ["context compacted", "chat/contextCompacted_ftpf87"],
  ["compacting context", "chat/compactingContext_p9krpc"],
  ["context compaction failed", "chat/contextCompactionFailed_38afi8"],
  ["tool updated", "chat/toolUpdated_91rel6"],
]);

function localizeToolActivityLabel(i18n: I18nInstance, label: string): string {
  const descriptor = TOOL_ACTIVITY_LABELS.get(label.toLowerCase());
  return descriptor ? i18n.t(descriptor) : label;
}

function toolWorkEntryHeading(workEntry: TimelineWorkEntry): string {
  if (!workEntry.toolTitle) {
    return capitalizePhrase(normalizeCompactToolLabel(workEntry.label));
  }
  return capitalizePhrase(normalizeCompactToolLabel(workEntry.toolTitle));
}

/**
 * The authoritative status of a sub-agent task row is the child thread's turn
 * state (level signal, synced via the shell stream); event-derived
 * `taskStatus` only fills in when the child thread is not in the store
 * (archived or pre-migration histories).
 */
function useTaskDisplayStatus(workEntry: TimelineWorkEntry): TaskDisplayStatus | null {
  const { activeThreadEnvironmentId } = use(TimelineRowCtx);
  const childThreadId = workEntry.childThreadId;
  const childStatus = useStore((state) => {
    if (!childThreadId) {
      return null;
    }
    const environmentState = selectEnvironmentState(state, activeThreadEnvironmentId);
    const childId = ThreadId.make(childThreadId);
    return deriveChildTaskStatus(
      environmentState.threadTurnStateById[childId]?.latestTurn,
      environmentState.threadSessionById[childId],
    );
  });
  if (workEntry.taskId === undefined) {
    return null;
  }
  return childStatus ?? workEntry.taskStatus ?? null;
}

function taskDisplayTone(status: TaskDisplayStatus): TimelineWorkEntry["tone"] {
  if (status === "running" || status === "working") return "thinking";
  if (status === "failed") return "error";
  return "info";
}

const WORK_ENTRY_TYPE_ICONS: Record<NonNullable<TimelineWorkEntry["itemType"]>, LucideIcon> = {
  command_execution: TerminalIcon,
  file_change: FilePenIcon,
  image_view: ImageIcon,
  mcp_tool_call: DatabaseIcon,
  web_search: SearchIcon,
  collab_agent_tool_call: BotIcon,
  dynamic_tool_call: PuzzleIcon,
};

function WorkEntryTypeIcon({ itemType }: { itemType: NonNullable<TimelineWorkEntry["itemType"]> }) {
  const Icon = WORK_ENTRY_TYPE_ICONS[itemType];
  return (
    <Icon aria-hidden="true" className="size-3 opacity-70" data-work-entry-type-icon={itemType} />
  );
}

function WorkEntryMarker() {
  return (
    <span
      aria-hidden="true"
      className="size-1 rounded-full bg-current opacity-40"
      data-work-entry-marker="true"
    />
  );
}

const WorkEntryRow = memo(function WorkEntryRow({ workEntry }: { workEntry: TimelineWorkEntry }) {
  const { i18n } = useTranslation();
  const { activeThreadEnvironmentId, timelineThreadId, workspaceRoot } = use(TimelineRowCtx);
  const displayStatus = useTaskDisplayStatus(workEntry);
  const tone = displayStatus ? taskDisplayTone(displayStatus) : workEntry.tone;
  const isPublic = workEntry.labelKey !== undefined;
  const baseHeading = isPublic
    ? workEntry.label
    : localizeToolActivityLabel(i18n, toolWorkEntryHeading(workEntry));
  const heading = displayStatus
    ? `${baseHeading} · ${taskStatusLabel(i18n, displayStatus)}`
    : baseHeading;
  const active = workEntry.active === true || workEntry.compactionPhase === "started";
  const iconType = workEntry.itemType ?? (displayStatus ? "collab_agent_tool_call" : undefined);

  return (
    <div
      data-work-entry="true"
      data-work-entry-id={workEntry.id}
      data-work-entry-active={active ? "true" : undefined}
      data-compaction-phase={workEntry.compactionPhase}
      className={cn(
        "rounded-lg px-1 py-1",
        workEntry.childThreadId && "cursor-pointer hover:bg-muted/40",
        active && "animate-pulse",
      )}
      onClick={
        workEntry.childThreadId
          ? () =>
              openSubAgentPanelForEntry(
                workEntry,
                scopeThreadRef(activeThreadEnvironmentId, timelineThreadId),
              )
          : undefined
      }
    >
      <div className="flex items-center gap-2 transition-[opacity,translate] duration-200">
        <span
          className={cn(
            "flex size-5 shrink-0 items-center justify-center",
            tone === "error" ? "text-rose-400" : "text-muted-foreground",
          )}
        >
          {workEntry.compactionPhase ? (
            <ShrinkIcon
              aria-hidden="true"
              className="size-3 opacity-70"
              data-work-entry-compaction-icon="true"
            />
          ) : iconType !== undefined ? (
            <WorkEntryTypeIcon itemType={iconType} />
          ) : (
            <WorkEntryMarker />
          )}
        </span>
        <div className="min-w-0 overflow-hidden">
          {isPublic ? (
            <p className={cn("truncate text-sm leading-6", workToneClass(tone))}>
              <span className={cn("text-foreground/80", workToneClass(tone))}>{heading}</span>
            </p>
          ) : (
            <LegacyWorkEntryLabel workEntry={workEntry} heading={heading} tone={tone} />
          )}
        </div>
      </div>
      {!isPublic ? (
        <LegacyChangedFiles workEntry={workEntry} workspaceRoot={workspaceRoot} />
      ) : null}
    </div>
  );
});

const WorkEntryGroup = memo(function WorkEntryGroup({ group }: { group: WorkLogGroup }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const more = group.entries.length - 1;
  const head = group.entries[0];
  const tone = head?.tone ?? "tool";

  return (
    <div
      data-work-entry-group="true"
      data-work-entry-group-id={group.id}
      data-work-entry-group-count={group.entries.length}
      data-work-entry-group-expanded={expanded ? "true" : "false"}
    >
      <button
        type="button"
        aria-expanded={expanded}
        data-work-entry-group-toggle="true"
        data-scroll-anchor-ignore
        onClick={() => setExpanded((value) => !value)}
        className="group flex max-w-full items-center gap-2 rounded-lg px-1 py-1 text-left text-sm leading-6"
      >
        <span className="flex size-5 shrink-0 items-center justify-center text-muted-foreground">
          {head?.itemType !== undefined ? (
            <WorkEntryTypeIcon itemType={head.itemType} />
          ) : (
            <WorkEntryMarker />
          )}
        </span>
        <span
          className={cn(
            "truncate text-foreground/80 transition-colors duration-150 group-hover:text-foreground",
            workToneClass(tone),
          )}
        >
          {group.label}
        </span>
        <span className="shrink-0 text-muted-foreground/55" data-work-entry-group-more="true">
          {t("chat/andMore_oy5mhn", { more })}
        </span>
        <ChevronRightIcon
          aria-hidden="true"
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground/50 transition-transform duration-200",
            expanded && "rotate-90",
          )}
        />
      </button>
      {expanded ? (
        <div className="space-y-0.5 pl-5" data-work-entry-group-entries="true">
          {group.entries.map((workEntry) => (
            <WorkEntryRow key={`work-row:${workEntry.id}`} workEntry={workEntry} />
          ))}
        </div>
      ) : null}
    </div>
  );
});

/** A step without a server label (stored before labeling, or a non-tool kind): its own words. */
function LegacyWorkEntryLabel({
  workEntry,
  heading,
  tone,
}: {
  workEntry: TimelineWorkEntry;
  heading: string;
  tone: TimelineWorkEntry["tone"];
}) {
  const { workspaceRoot } = use(TimelineRowCtx);
  const rawPreview = workEntryPreview(workEntry, workspaceRoot);
  const preview =
    rawPreview &&
    normalizeCompactToolLabel(rawPreview).toLowerCase() ===
      normalizeCompactToolLabel(heading).toLowerCase()
      ? null
      : rawPreview;
  const rawCommand = workEntryRawCommand(workEntry);
  const displayText = preview ? `${heading} - ${preview}` : heading;

  if (rawCommand) {
    return (
      <div className="max-w-full">
        <Tooltip>
          <TooltipTrigger
            closeDelay={0}
            delay={75}
            render={
              <p
                className={cn(
                  "truncate rounded-sm text-xs leading-5",
                  workToneClass(tone),
                  preview ? "text-muted-foreground/70" : "",
                )}
                aria-label={displayText}
              />
            }
          >
            <span className={cn("text-foreground/80", workToneClass(tone))}>{heading}</span>
            {preview && <span className="text-muted-foreground/55"> - {preview}</span>}
          </TooltipTrigger>
          <TooltipPopup
            align="start"
            className="max-w-[min(56rem,calc(100vw-2rem))] px-0 py-0"
            side="top"
          >
            <div className="max-w-[min(56rem,calc(100vw-2rem))] overflow-x-auto px-1.5 py-1 font-mono text-[11px] leading-4 whitespace-nowrap">
              {rawCommand}
            </div>
          </TooltipPopup>
        </Tooltip>
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger className="block min-w-0 w-full text-left" aria-label={displayText}>
        <p
          className={cn(
            "truncate text-sm leading-6",
            workToneClass(tone),
            preview ? "text-muted-foreground/70" : "",
          )}
        >
          <span className={cn("text-foreground/80", workToneClass(tone))}>{heading}</span>
          {preview && <span className="text-muted-foreground/55"> - {preview}</span>}
        </p>
      </TooltipTrigger>
      <TooltipPopup className="max-w-[min(720px,calc(100vw-2rem))]">
        <p className="whitespace-pre-wrap wrap-break-word text-xs leading-5">{displayText}</p>
      </TooltipPopup>
    </Tooltip>
  );
}

function LegacyChangedFiles({
  workEntry,
  workspaceRoot,
}: {
  workEntry: TimelineWorkEntry;
  workspaceRoot: string | undefined;
}) {
  const changedFiles = workEntry.changedFiles ?? [];
  const previewIsChangedFiles = changedFiles.length > 0 && !workEntry.command && !workEntry.detail;
  if (changedFiles.length === 0 || previewIsChangedFiles) {
    return null;
  }
  return (
    <div className="mt-1 flex flex-wrap gap-1 pl-6">
      {changedFiles.slice(0, 4).map((filePath) => {
        const displayPath = formatWorkspaceRelativePath(filePath, workspaceRoot);
        return (
          <Tooltip key={`${workEntry.id}:${filePath}`}>
            <TooltipTrigger
              render={
                <span
                  className="rounded-md border border-border/55 bg-background/75 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground/75"
                  aria-label={displayPath}
                />
              }
            >
              {displayPath}
            </TooltipTrigger>
            <TooltipPopup side="top" className="max-w-[min(40rem,calc(100vw-2rem))]">
              <span className="font-mono text-[11px] whitespace-nowrap">{displayPath}</span>
            </TooltipPopup>
          </Tooltip>
        );
      })}
      {changedFiles.length > 4 && (
        <span className="px-1 text-[10px] text-muted-foreground/55">
          +{changedFiles.length - 4}
        </span>
      )}
    </div>
  );
}
